import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ops } from '../api/operations';
import type { Release, ReleasePin } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Chip';
import { ChevronDown, GitBranch, Plus } from '../ui/icons';
import { NewBranchDialog } from '../pages/history/BranchDialogs';
import { Popover } from '../ui/Popover';
import { TextInput } from '../ui/inputs';
import { useQuery } from '../workspace/cache';
import { dirtyBranches } from '../workspace/react';
import { useGame } from './GameContext';
import { routes } from './routes';

/**
 * The branch selector (spec section 3.3): the current branch with chips for the environments whose default pin was built
 * from it; switching rewrites the branch segment of the current URL and nothing else (Q-N-2). The popover's footer has
 * New branch… (section 12.1, `content_write`): the history toolbar's dialog, landing on the same page of the new branch.
 */
export function BranchSelector({ current }: { current: string | null }) {
  const { org, game, branches, environments, can } = useGame();
  const { client } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [filter, setFilter] = useState('');
  const [creating, setCreating] = useState(false);
  // New branch… forks the current branch's head (the default branch's off a branch page); no drafts move (section 12.1)
  const from = current ?? branches.find((b) => b.isDefault)?.name ?? branches[0]?.name ?? null;

  // which environments run a release of which branch: the default pin of each environment → its release → branch name
  const pinsQ = useQuery(`game:${org}/${game.slug}:pins`, async () => {
    const out: Record<string, string> = {};
    await Promise.all(
      environments.map(async (env) => {
        try {
          const pins = await client.data(ops.listPins, { path: { org, game: game.slug, env: env.slug }, query: { active: 'true', limit: 50 } });
          const active = (pins.items as ReleasePin[]).find((p) => p.isDefault && !p.revokedAt);
          if (!active) return;
          const release = (await client.data(ops.getRelease, { path: { org, game: game.slug, release: active.releaseId } })) as Release;
          out[env.kind] = release.branchName;
        } catch {
          // no pin, or no permission: no chip
        }
      }),
    );
    return out;
  });
  const envsOf = (branch: string) => Object.entries(pinsQ.data ?? {}).filter(([, b]) => b === branch).map(([kind]) => kind);
  const dirty = useMemo(() => new Map(dirtyBranches(game.id).map((d) => [d.branch, d.count])), [game.id, anchor]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchTo = (name: string) => {
    setAnchor(null);
    if (name === current) return;
    if (current) {
      // rewrite only the branch segment; the page and its query stay
      const prefix = routes.branch(org, game.slug, current, '');
      const rest = location.pathname.startsWith(prefix) ? location.pathname.slice(prefix.length) : 'templates';
      navigate(routes.branch(org, game.slug, name, rest) + location.search);
    } else {
      navigate(routes.templates(org, game.slug, name));
    }
  };

  const shown = branches.filter((b) => b.name.toLowerCase().includes(filter.trim().toLowerCase()));
  return (
    <>
      <Button className="branch-button" onClick={(e) => setAnchor(e.currentTarget)} aria-haspopup="dialog" aria-expanded={anchor !== null} title={current ?? 'Pick a branch'}>
        <GitBranch aria-hidden="true" />
        <span className="name">{current ?? 'Pick a branch'}</span>
        {current && envsOf(current).map((k) => <Badge key={k} tone={k === 'prod' ? 'danger' : k === 'stage' ? 'warning' : 'success'}>{k}</Badge>)}
        <ChevronDown aria-hidden="true" />
      </Button>
      <Popover anchor={anchor} open={anchor !== null} onClose={() => setAnchor(null)} label="Branches">
        <TextInput size="s" aria-label="Filter branches" placeholder="Filter branches…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <div className="branch-list" role="listbox" aria-label="Branches">
          {shown.map((b) => (
            <button key={b.id} type="button" role="option" aria-selected={b.name === current} className={`branch-row ${b.name === current ? 'active' : ''}`} onClick={() => switchTo(b.name)}>
              <span className="name">{b.name}</span>
              {b.isProtected && <Badge tone="info" title="Protected: changes land through merge requests">protected</Badge>}
              {envsOf(b.name).map((k) => (
                <Badge key={k} tone={k === 'prod' ? 'danger' : k === 'stage' ? 'warning' : 'success'}>
                  {k}
                </Badge>
              ))}
              {dirty.get(b.name) ? <Badge tone="warning" title="Unsaved changes on this branch">{dirty.get(b.name)} unsaved</Badge> : null}
            </button>
          ))}
          {shown.length === 0 && <div className="muted small" style={{ padding: 8 }}>No branch matches.</div>}
        </div>
        {can('content_write') && from && (
          <div className="branch-popover-footer">
            <Button
              size="s"
              variant="ghost"
              icon={<Plus aria-hidden="true" />}
              onClick={() => {
                setAnchor(null);
                setCreating(true);
              }}
            >
              New branch…
            </Button>
          </div>
        )}
      </Popover>
      {from && <NewBranchDialog open={creating} onClose={() => setCreating(false)} branch={from} fromSha={null} samePage={current !== null} />}
    </>
  );
}
